import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import type { AccountRow } from '@/api/accounts'
import { MT5_ABILITY } from '@/constants/dicts'

const KEY = 'zr.portal.currentAccount'

/**
 * 「当前账户」—— 顶栏账户选择器选中的那个（§2 术语 / §8.2）。
 *
 * 首页资金概览、我的账户明细、入金 / 出金默认账户、平台下载的真实账户卡
 * 全都跟着它走；切换后全站联动。**模拟账户不参与「当前账户」**——
 * 它只在平台下载页作为一张只读示范卡出现（R-15）。
 */
export const useAccountStore = defineStore('account', () => {
  const all = ref<AccountRow[]>([])
  const currentId = ref<number | null>(null)

  /** 真实账户（demo=false），出入金与资金概览只认这些 */
  const live = computed(() => all.value.filter((a) => !a.demo))
  const demo = computed(() => all.value.find((a) => a.demo) ?? null)

  /** 出入金选择列表：已禁用 / 已关闭不出现（§8.1） */
  const selectable = computed(() => live.value.filter((a) => MT5_ABILITY[a.status].listed))

  const current = computed(
    () => live.value.find((a) => a.id === currentId.value) ?? live.value[0] ?? null,
  )

  /** 仅 1 个账户时所有账户选择控件降级为只读一行，不显示下拉（§8.1） */
  const multi = computed(() => live.value.length >= 2)

  function setAll(rows: AccountRow[]) {
    all.value = rows
    if (!rows.some((a) => a.id === currentId.value)) {
      currentId.value = rows.find((a) => !a.demo)?.id ?? null
    }
  }

  function select(id: number) {
    currentId.value = id
  }

  /**
   * 换客户时清空（由 `stores/session.ts` 统一调用）。
   *
   * `all` 只活在内存里，而退出 / 登录都是单页跳转、不刷新页面 —— 不主动清就会把
   * 上一位客户的账号显示给下一位（平台下载页那两张登录卡是最显眼的一处）。
   * `currentId` 置 null 会由下面的 watch 顺手删掉 localStorage 里那把钥匙：
   * 账户 id 全局唯一、留着不至于张冠李戴，但留着也没有意义。
   */
  function reset() {
    all.value = []
    currentId.value = null
  }

  // 记住上次选的账户，刷新后不跳回第一个
  try {
    const saved = localStorage.getItem(KEY)
    if (saved) currentId.value = Number(saved)
  } catch {
    /* ignore */
  }
  watch(currentId, (v) => {
    try {
      if (v == null) localStorage.removeItem(KEY)
      else localStorage.setItem(KEY, String(v))
    } catch {
      /* ignore */
    }
  })

  return { all, live, demo, selectable, current, currentId, multi, setAll, select, reset }
})
